import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import {
  ActiveAlertRulesTable,
  AlertRuleDialog,
  ApprovalQueue,
  ProposeNewButton,
} from "../../../components/AthenaPricingEngine/alert-rules";
import { Skeleton } from "../../../ui/skeleton";
import {
  useDecideRbpeAlertRuleProposal,
  useProposeRbpeAlertRule,
  useRbpeAlertRuleProposals,
  useRbpeAlertRules,
} from "../lib/api/rbpe-alert-rules";

export default function ManageAlerts() {
  const [dialogOpen, setDialogOpen] = useState(false);

  const rulesQuery = useRbpeAlertRules();
  const proposalsQuery = useRbpeAlertRuleProposals();
  const propose = useProposeRbpeAlertRule();
  const decide = useDecideRbpeAlertRuleProposal();

  const isLoading = rulesQuery.isPending || proposalsQuery.isPending;
  const isError = rulesQuery.isError || proposalsQuery.isError;

  return (
    <div>
      <Link to="/risk-pricing-engine/exceptions" className="text-xs text-amber hover:underline">
        ← Back to Exceptions
      </Link>

      <PageHeader
        title="Manage Alerts"
        subtitle="Configure what counts as a pricing exception"
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          Could not load alert rules.
        </div>
      ) : isLoading ? (
        <div className="mt-8 space-y-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        <>
          <Panel
            className="mt-8"
            title="Active Alert Rules"
            description="What currently trips an exception, and who hears about it."
            right={<ProposeNewButton onClick={() => setDialogOpen(true)} />}
          >
            <ActiveAlertRulesTable rules={rulesQuery.data ?? []} />
          </Panel>

          <Panel
            className="mt-6"
            title="Approval Queue"
            description="Proposed threshold changes waiting on a second pair of eyes."
          >
            <ApprovalQueue
              proposals={proposalsQuery.data ?? []}
              isDeciding={decide.isPending}
              onDecide={(id, status) => decide.mutateAsync({ id, status })}
            />
          </Panel>

          <p className="mt-6 text-xs text-muted-foreground">
            Superset runs the production calibration — this page is the configuration and review
            surface. Changes take effect on the next scheduled sync once approved.
          </p>

          <AlertRuleDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            isSubmitting={propose.isPending}
            onSubmit={(draft) => propose.mutateAsync(draft)}
          />
        </>
      )}
    </div>
  );
}
